import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ReportFilterBar, defaultFilters, paramsFromFilters } from './reports/ReportFilterBar';
import { SummaryTab } from './reports/SummaryTab';
import { InvoicesTab } from './reports/InvoicesTab';
import { PaymentsTab } from './reports/PaymentsTab';
import { ReceivablesTab } from './reports/ReceivablesTab';
import { OverdueTab } from './reports/OverdueTab';
import { PaidTab } from './reports/PaidTab';
import { TaxTab } from './reports/TaxTab';
import { AuditTab } from './reports/AuditTab';
import { QuotationsTab } from './reports/QuotationsTab';
import { ClientsTab } from './reports/ClientsTab';
import { ProductsTab } from './reports/ProductsTab';
import { ClosingPackButton } from './reports/ClosingPackButton';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const TABS = [
  { value: 'summary', label: 'Ringkasan' },
  { value: 'invoices', label: 'Invoice' },
  { value: 'payments', label: 'Pembayaran' },
  { value: 'receivables', label: 'Piutang' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Lunas' },
  { value: 'tax', label: 'Pajak' },
  { value: 'quotations', label: 'Penawaran' },
  { value: 'clients', label: 'Client' },
  { value: 'products_packages', label: 'Produk & Paket' },
  { value: 'audit', label: 'Audit Data' },
];

export default function Reports() {
  const [activeTab, setActiveTab] = useState('summary');
  const [filters, setFilters] = useState(defaultFilters());
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [tabData, setTabData] = useState({});
  const [loadingTab, setLoadingTab] = useState({});
  const [clients, setClients] = useState([]);
  const [auditIncludeReviewed, setAuditIncludeReviewed] = useState(false);

  // Load client list once for filter
  useEffect(() => {
    api.get('/clients')
      .then(({ data }) => setClients(data || []))
      .catch((err) => {
        // Non-fatal: filter UI still functions, but we surface the failure
        // so dev/ops can diagnose dropped requests or auth issues.
        console.error('[Reports] failed to load clients for filter', err);
      });
  }, []);

  const apiParams = useMemo(() => paramsFromFilters(appliedFilters), [appliedFilters]);

  const fetchTab = useCallback(async (tab) => {
    setLoadingTab(p => ({ ...p, [tab]: true }));
    try {
      // Backend URL slug: most tabs use their value directly, except "products_packages" which uses dash
      const urlSlug = tab === 'products_packages' ? 'products-packages' : tab;
      const params = tab === 'audit' ? { include_reviewed: auditIncludeReviewed } : apiParams;
      const { data } = await api.get(`/reports/v2/${urlSlug}`, { params });
      setTabData(p => ({ ...p, [tab]: data }));
    } catch (e) {
      console.error('[Reports] failed to load tab', tab, e);
      toast.error(`Gagal memuat laporan ${tab}`);
    } finally {
      setLoadingTab(p => ({ ...p, [tab]: false }));
    }
  }, [apiParams, auditIncludeReviewed]);

  // Re-fetch the active tab when filters or active tab change
  useEffect(() => { fetchTab(activeTab); }, [activeTab, fetchTab]);

  const apply = () => setAppliedFilters({ ...filters });
  const reset = () => {
    const def = defaultFilters();
    setFilters(def); setAppliedFilters(def);
  };

  const renderActive = () => {
    const d = tabData[activeTab];
    const loading = loadingTab[activeTab];
    const af = apiParams;
    if (activeTab === 'summary') return <SummaryTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'invoices') return <InvoicesTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'payments') return <PaymentsTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'receivables') return <ReceivablesTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'overdue') return <OverdueTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'paid') return <PaidTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'tax') return <TaxTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'quotations') return <QuotationsTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'clients') return <ClientsTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'products_packages') return <ProductsTab data={d} loading={loading} filters={af} />;
    if (activeTab === 'audit') return (
      <AuditTab
        data={d} loading={loading} filters={af}
        onRefresh={() => fetchTab('audit')}
        includeReviewed={auditIncludeReviewed}
        setIncludeReviewed={(v) => { setAuditIncludeReviewed(v); }}
      />
    );
    return null;
  };

  return (
    <div className="space-y-4 sm:space-y-5" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-100">Laporan</h1>
          <p className="text-sm text-slate-400">Pusat kontrol akuntansi — invoice, pembayaran, piutang, pajak, dan audit data.</p>
        </div>
        <ClosingPackButton filters={apiParams} />
      </div>

      <ReportFilterBar filters={filters} setFilters={setFilters} onApply={apply} onReset={reset} clients={clients} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-2 px-2">
          <TabsList className="bg-card border border-border" data-testid="reports-tabs-list">
            {TABS.map(t => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`reports-tab-${t.value}`} className="text-xs sm:text-sm whitespace-nowrap">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {TABS.map(t => (
          <TabsContent key={t.value} value={t.value} className="mt-4">
            {activeTab === t.value ? (
              loadingTab[t.value] && !tabData[t.value]
                ? <Skeleton className="h-[400px] w-full" />
                : renderActive()
            ) : null}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
